/**
 * Clean-up of HTML pasted into the RTF editor (#6465).
 *
 * The toolbar offers bold, italic, underline, link, bullet/ordered lists and indent, and the schema
 * in `rtfExtensions` is limited to exactly that. Word sends a great deal more: heading levels, fonts,
 * sizes, colours, classes and `mso-*` noise. Left alone it arrives looking bold while being something
 * the buttons cannot touch, which is what the ticket reports.
 *
 * So the rule here is: keep what the toolbar can produce — and keep it as real marks the buttons can
 * toggle — and drop the rest without ever dropping the text it wrapped.
 */

/** Kept as they are. Every other element is either turned into a paragraph or unwrapped. */
const ALLOWED_TAGS = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'A', 'UL', 'OL', 'LI'])

/** Removed with their content: they carry no text of the document. */
const DROPPED_TAGS = new Set(['STYLE', 'SCRIPT', 'META', 'LINK', 'TITLE', 'HEAD', 'O:P', 'XML', 'IMG'])

/**
 * Turned into paragraphs rather than unwrapped. Unwrapping them would merge their text into the
 * neighbouring block — two Word headings in a row would become one run-on paragraph.
 */
const BLOCK_TAGS = new Set([
   'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
   'DIV', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'ASIDE', 'MAIN',
   'BLOCKQUOTE', 'PRE', 'ADDRESS', 'FIGCAPTION', 'CAPTION',
   'TD', 'TH', 'DT', 'DD',
])

const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6'])

/** Same thresholds as TipTap's own Bold parse rule, so pasted weight becomes a mark the button owns. */
const BOLD_WEIGHT = /^(bold(er)?|[5-9]\d{2,})$/
const REGULAR_WEIGHT = /^(normal|[1-4]\d{2,})$/

/** Markers `rtfListStyles` understands. Anything else falls back to the positional cascade. */
const ORDERED_LIST_TYPE = /^[1aAiI]$/

/** `Indent.parseHTML` reads `margin-left` in pixels only, so that is the only length worth keeping. */
const PX_LENGTH = /^-?\d+(\.\d+)?px$/

const SAFE_HREF = /^(https?:|mailto:|tel:|#|\/)/i

/** Read one declaration. `getPropertyValue` always answers with a string, unlike the shorthand fields. */
const style = (element: HTMLElement, property: string): string => element.style.getPropertyValue(property).trim().toLowerCase()

/** The emphasis an inline style asks for, as the tags that carry it in this editor. */
const emphasisTagsFromStyle = (element: HTMLElement): string[] => {
   const tags: string[] = []
   const fontStyle = style(element, 'font-style')
   const underline = `${style(element, 'text-decoration')} ${style(element, 'text-decoration-line')}`

   if (BOLD_WEIGHT.test(style(element, 'font-weight'))) {
      tags.push('strong')
   }
   if (fontStyle === 'italic' || fontStyle === 'oblique') {
      tags.push('em')
   }
   if (underline.includes('underline')) {
      tags.push('u')
   }

   return tags
}

/** Move everything inside `element` into a nest of emphasis tags, innermost last. */
const wrapChildren = (element: HTMLElement, tags: string[]): void => {
   const doc = element.ownerDocument
   const outermost = doc.createElement(tags[0])
   let innermost = outermost

   tags.slice(1).forEach((tag) => {
      const next = doc.createElement(tag)
      innermost.appendChild(next)
      innermost = next
   })

   while (element.firstChild) {
      innermost.appendChild(element.firstChild)
   }
   element.appendChild(outermost)
}

/** Replace `element` with `tag`, keeping its children. */
const rename = (element: HTMLElement, tag: string): HTMLElement => {
   const replacement = element.ownerDocument.createElement(tag)

   while (element.firstChild) {
      replacement.appendChild(element.firstChild)
   }
   element.replaceWith(replacement)

   return replacement
}

/** Replace `element` with its children, keeping the text it wrapped. */
const unwrap = (element: HTMLElement): void => {
   element.replaceWith(...Array.from(element.childNodes))
}

/** Strip every attribute except the few the editor itself can produce. */
const keepOnlySupportedAttributes = (element: HTMLElement): void => {
   const tag = element.tagName.toUpperCase()
   const href = tag === 'A' ? element.getAttribute('href') : null
   const type = tag === 'OL' ? element.getAttribute('type') : null
   const marginLeft = tag === 'P' ? style(element, 'margin-left') : ''

   Array.from(element.attributes).forEach((attribute) => element.removeAttribute(attribute.name))

   if (href && SAFE_HREF.test(href.trim())) {
      element.setAttribute('href', href.trim())
   }
   if (type && ORDERED_LIST_TYPE.test(type)) {
      element.setAttribute('type', type)
   }
   if (PX_LENGTH.test(marginLeft)) {
      element.style.setProperty('margin-left', marginLeft)
   }
}

const cleanChildren = (parent: Element): void => {
   Array.from(parent.childNodes).forEach((node) => {
      if (node.nodeType === Node.COMMENT_NODE) {
         // Word's conditional comments, e.g. `<![if !supportLists]>` around a literal list marker.
         node.remove()
         return
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
         return
      }

      const element = node as HTMLElement
      const tag = element.tagName.toUpperCase()

      if (DROPPED_TAGS.has(tag)) {
         element.remove()
         return
      }

      cleanChildren(element)

      // A heading is the case of #6465: its text must come in as body text the author can then
      // make bold, so its own emphasis is dropped rather than translated.
      if (!HEADING_TAGS.has(tag)) {
         const emphasis = emphasisTagsFromStyle(element)

         if (emphasis.length) {
            wrapChildren(element, emphasis)
         }
      }

      // `<b style="font-weight:normal">` is how Google Docs wraps a whole document. Kept as `<b>`
      // it would turn every pasted word bold — the very thing being reported.
      if ((tag === 'B' || tag === 'STRONG') && REGULAR_WEIGHT.test(style(element, 'font-weight'))) {
         unwrap(element)
         return
      }

      if (BLOCK_TAGS.has(tag)) {
         keepOnlySupportedAttributes(rename(element, 'p'))
         return
      }
      if (!ALLOWED_TAGS.has(tag)) {
         unwrap(element)
         return
      }

      keepOnlySupportedAttributes(element)
   })
}

export const sanitizePastedHtml = (html: string): string => {
   // Content copied from a ProseMirror editor — another krav field, or this one — carries its own
   // slice metadata. It already matches the schema, and rewriting it would lose the open depths that
   // keep list nesting and indent intact across a copy.
   if (html.includes('data-pm-slice')) {
      return html
   }

   const document = new DOMParser().parseFromString(html, 'text/html')

   cleanChildren(document.body)

   return document.body.innerHTML
}
